import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Play, Pause, Headphones } from 'lucide-react';
import { api } from '@/lib/api';
import { LoadingState, ErrorState } from '@/components/states';
import { Logo } from '@/components/Logo';
import { ShareButton } from '@/components/actions';
import { usePlayer } from '@/context/PlayerContext';

const FALLBACK = 'https://images.pexels.com/photos/167092/pexels-photo-167092.jpeg?auto=compress&cs=tinysrgb&w=800';

export default function PublicSong() {
  const { slug } = useParams();
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { playTrack, current, playing, toggle } = usePlayer();

  useEffect(() => {
    api.get(`/public/song/${slug}`)
      .then(({ data }) => setSong(data))
      .catch(() => setError('Ce morceau est introuvable ou privé.'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen"><LoadingState /></div>;
  if (error) return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center">
      <div className="mb-8 flex justify-center"><Logo variant="full" className="h-16" /></div>
      <ErrorState message={error} />
      <Link to="/" className="mt-4 inline-block text-brand hover:underline">Retour à l'accueil</Link>
    </div>
  );

  const isCurrent = current?.song_id === song.id;
  const track = {
    song_id: song.id, title: song.title, slug: song.slug, cover_url: song.cover_url,
    audio_url: song.audio_url, style_text: song.style_text, duration_seconds: song.duration_seconds,
    creator: song.creator?.display_name,
  };

  return (
    <div className="min-h-screen pb-32">
      <header className="glass sticky top-0 z-40 flex items-center justify-between border-b border-white/10 px-4 py-3 sm:px-8">
        <Logo variant="full" className="h-10 sm:h-12" />
        <Link to="/create" className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-black btn-glow" data-testid="public-create-cta">
          Crée ta musique
        </Link>
      </header>

      <div className="mx-auto max-w-3xl px-4 pt-10 sm:pt-16">
        <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
          <div className="relative h-56 w-56 flex-shrink-0 overflow-hidden rounded-3xl card-surface" data-testid="public-song-cover">
            <img src={song.cover_url || FALLBACK} alt={song.title} className="h-full w-full object-cover" />
          </div>
          <div className="flex-1">
            <span className="font-mono text-xs uppercase tracking-widest text-brand">{song.style_text || 'MyMusic IA'}</span>
            <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-white">{song.title}</h1>
            <p className="mt-2 text-muted-foreground">par {song.creator?.display_name || 'Artiste'}</p>
            <p className="mt-1 flex items-center justify-center gap-1 font-mono text-sm text-muted-foreground sm:justify-start">
              <Headphones className="h-4 w-4" /> {song.plays} écoutes
            </p>
            <div className="mt-5 flex items-center justify-center gap-3 sm:justify-start">
              <button onClick={() => (isCurrent ? toggle() : playTrack(track, [track]))}
                      className="flex items-center gap-2 rounded-full bg-brand px-6 py-3 font-semibold text-black btn-glow" data-testid="public-play-button">
                {isCurrent && playing ? <><Pause className="h-5 w-5" /> Pause</> : <><Play className="h-5 w-5 fill-black" /> Écouter</>}
              </button>
              <ShareButton slug={song.slug} title={song.title} creator={song.creator?.display_name} label />
            </div>
          </div>
        </div>

        {song.lyrics && (
          <div className="card-surface mt-10 p-6" data-testid="public-lyrics">
            <h2 className="mb-3 font-display text-lg font-semibold text-white">Paroles</h2>
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-muted-foreground">{song.lyrics}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
